import {NO, UIButton, UIColor, UIComponentView, UIView, YES} from "uicore-ts"
import {IF, ValueOf} from "uicore-ts/compiledScripts/UIObject"
import {BSColor} from "./BSColor"
import {RHAltModeKeyable, RHAltModeKeyHandler} from "./Keyboard navigation/RHAltModeKeyHandler"
import {RHColor} from "./RHColor"
import {RHTooltip} from "./RHTooltip"
import {RHButtons, type RHButtonVariantTokens} from "./Utils_slop/RHDesignTokens"

export type RHButtonVariant =
    | "primary"
    | "secondary"
    | "success"
    | "brandOrange"
    | "softWarning"
    | "quietWarning"
    | "softDanger"
    | "tableActionSuccess"
    | "messageAnswer"
    | "messageCopy"
    | "danger"
    | "warning"
    | "info"
    | "light"
    | "dark"
    | "link"

@UIComponentView
export class RHButton extends UIButton implements RHAltModeKeyable {
    
    // SVG Icon functions
    static ICON_DOWNLOAD(strokeWidth: number = 1, size: number = 16): string {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
  <path stroke="currentColor" stroke-width="${strokeWidth}" d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
  <path stroke="currentColor" stroke-width="${strokeWidth}" d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
</svg>`
    }
    
    // Configurable constant for sheen effect intensity (0.0 to 0.5)
    static sheenIntensity: number = 0.15
    
    // Hover never moves the button: a moved hit box slides out from under a resting cursor and hover flickers in a loop.
    
    _variant: RHButtonVariant = "primary"
    _borderPulseGeneration = 0
    _borderPulseView?: UIView
    _borderPulseAnimation?: ReturnType<typeof UIView.animateViewOrViewsWithDurationDelayAndFunction>
    
    // MARK: - RHAltModeKeyable
    
    _altModeKeyHandler?: RHAltModeKeyHandler
    
    get altModeKey(): string | undefined {
        return this._altModeKeyHandler?.altKey
    }
    
    set altModeKey(key: string | undefined) {
        this._setAltModeKey(key, undefined)
    }
    
    _setAltModeKey(key: string | undefined, displayView: UIView | undefined) {
        if (this._altModeKeyHandler) {
            this._altModeKeyHandler.deregister()
            this._altModeKeyHandler = undefined
        }
        if (key) {
            this._altModeKeyHandler = new RHAltModeKeyHandler(key, this, displayView)
        }
    }
    // CSS-sized well past the button's own bounds (aspect-ratio hack below), so it
    // must not count toward intrinsic sizing — otherwise any button whose height is
    // derived via rectangleWithHeight(button) measures thousands of pixels tall.
    _rippleView: UIView = new UIView().configuredWithObject({
        alpha: 0,
        hasWeakFrame: YES,
        style: {
            position: "absolute",
            borderRadius: "50%",
            backgroundColor: RHButtons.rippleBackgroundColor,
            pointerEvents: "none",
            transform: "translate(-50%, -50%) scale(0)",
            transition: RHButtons.rippleTransition,
            top: "50%",
            left: "50%",
            width: "250%",
            paddingBottom: "250%" // Aspect ratio hack
        }
    }).addedAsSubviewToView(this)
    
    
    constructor(elementID?: string) {
        
        // Pass "button" to super to ensure correct HTML tag
        super(elementID, "button")
        
        
        // Use configureWithObject for deep style assignment
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                display: "inline-block",
                fontWeight: RHButtons.fontWeight,
                lineHeight: RHButtons.lineHeight,
                textAlign: "center",
                textDecoration: "none",
                verticalAlign: "middle",
                cursor: "pointer",
                userSelect: "none",
                border: "1px solid transparent",
                padding: RHButtons.padding,
                fontSize: RHButtons.fontSize,
                borderRadius: RHButtons.borderRadius,
                fontFamily: RHButtons.fontFamily,
                // Start with no transition
                transition: "",
                boxSizing: "border-box",
                overflow: "hidden" // Clips ripple
            }
        })
        
        // FIX: Configure.titleLabel to prevent visual jumping ("glitch") and ensure stable centering
        if (this.titleLabel) {
            this.titleLabel.configureWithObject({
                style: {
                    zIndex: "10",
                    lineHeight: RHButtons.lineHeight,
                    overflow: "visible",
                    textOverflow: "clip",
                    willChange: "auto"       // static button text should stay on the normal text rendering path; forcing transform compositing makes text hazier on Windows/Chromium
                }
            })
            this._applyTitleLabelTextClippingFix()
        }
        
        this.contentPadding = RHButtons.contentPadding
        
        // Apply default variant
        this.setVariant("primary")
        
    }
    
    
    private _applyTitleLabelTextClippingFix() {
        if (!this.titleLabel) {
            return
        }
        const titleTextElement = this.titleLabel.viewHTMLElement.querySelector(
            `#${this.titleLabel.elementID}_textElement`
        ) as HTMLElement | null
        if (titleTextElement) {
            titleTextElement.style.overflow = "visible"
            titleTextElement.style.textOverflow = "clip"
        }
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        if (this._borderPulseView?.superview === this) {
            // The native button's 1px border reduces the child's containing block.
            this._borderPulseView.weakFrame = this.contentBoundsWithInset(0).rectangleWithInsets(0, 2, 2, 0)
        }
        this._applyTitleLabelTextClippingFix()
        // UIButton.layoutSubviews falls back to the raw label text for the native
        // hover tooltip, so a markup label (e.g. an inline SVG icon) would show
        // its HTML verbatim on hover. Strip it to plain text; an icon-only label
        // strips to "" and shows no tooltip at all.
        if (this.hoverText?.includes("<")) {
            this.hoverText = RHTooltip.plainTextFromMarkup(this.hoverText)
        }
    }

    startBorderPulseAnimationWithColor(color: UIColor) {
        this.stopBorderPulseAnimation()
        if (!this.superview) {
            return
        }
        const pulseView = this._borderPulseView ?? new UIView().configuredWithObject({
            alpha: 0,
            hasWeakFrame: YES,
            userInteractionEnabled: NO,
            style: { pointerEvents: "none", boxSizing: "border-box" }
        })
        if (!this._borderPulseView) {
            pulseView.setPosition(0, undefined, undefined, 0)
        }
        this._borderPulseView = pulseView
        pulseView.configureWithObject({
            style: {
                border: `${RHButtons.borderPulseWidth}px solid ${color.stringValue}`,
                borderRadius: RHButtons.borderRadius
            }
        })
        pulseView.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
        const generation = this._borderPulseGeneration
        const numberOfPhases = RHButtons.numberOfBorderPulses * 2
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            pulseView.alpha = 1
        }
        else {
            for (let phase = 0; phase < numberOfPhases; phase++) {
                this.performFunctionWithDelay(phase * RHButtons.borderPulseHalfDuration, () => {
                    if (generation !== this._borderPulseGeneration) {
                        return
                    }
                    this._borderPulseAnimation?.didFinish()
                    this._borderPulseAnimation = UIView.animateViewOrViewsWithDurationDelayAndFunction(
                        pulseView, RHButtons.borderPulseHalfDuration, 0, "ease-in-out",
                        () => { pulseView.alpha = IF(phase % 2 === 0)(() => 1).ELSE(() => 0) },
                        () => {}, "opacity"
                    )
                })
            }
        }
        this.performFunctionWithDelay(numberOfPhases * RHButtons.borderPulseHalfDuration, () => {
            if (generation === this._borderPulseGeneration) {
                this.stopBorderPulseAnimation()
            }
        })
    }

    stopBorderPulseAnimation() {
        this._borderPulseGeneration++
        this._borderPulseAnimation?.didFinish()
        this._borderPulseAnimation = undefined
        this._borderPulseView?.removeFromSuperview()
        if (this._borderPulseView) {
            this._borderPulseView.alpha = 0
        }
    }

    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        this.stopBorderPulseAnimation()
    }
    
    
    
    get variant(): RHButtonVariant {
        return this._variant
    }
    
    set variant(value: RHButtonVariant) {
        this.setVariant(value)
    }
    
    private designTokensForVariant(variant: RHButtonVariant): RHButtonVariantTokens | undefined {
        const candidate = RHButtons[variant as keyof typeof RHButtons]
        return this.isButtonVariantTokens(candidate) ? candidate : undefined
    }


    private isButtonVariantTokens(candidate: (typeof RHButtons)[keyof typeof RHButtons]): candidate is RHButtonVariantTokens {
        return typeof candidate === "object" &&
            candidate !== null &&
            "textColor" in candidate &&
            "backgroundColor" in candidate &&
            "backgroundHoverColor" in candidate &&
            "backgroundActiveColor" in candidate &&
            "borderColor" in candidate &&
            "borderHoverColor" in candidate &&
            "borderActiveColor" in candidate &&
            "gradient" in candidate &&
            "gradientHover" in candidate &&
            "gradientActive" in candidate &&
            "shadow" in candidate &&
            "shadowHover" in candidate &&
            "shadowActive" in candidate
    }


    private focusOutlineForVariant(variant: RHButtonVariant) {
        const variantTokens = this.designTokensForVariant(variant)
        if (variantTokens) {
            return variantTokens.focusOutline ?? RHButtons.focusOutline
        }
        return RHButtons.focusOutline
    }
    
    setVariant(variant: RHButtonVariant) {
        this.stopBorderPulseAnimation()
        this._variant = variant
        
        // Use configureWithObject to batch update color specifier
        const colorConfig = this._colorConfigForVariant(variant)
        
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            colors: colorConfig
        })
        
        this.updateContentForCurrentState()
    }
    
    // Helper to map BSColor to UIButtonColorSpecifier structure
    _colorConfigForVariant(variant: RHButtonVariant) {
        const variantTokens = this.designTokensForVariant(variant)
        if (variantTokens) {
            return {
                titleLabel: {
                    normal: variantTokens.textColor,
                    hovered: variantTokens.textColor,
                    highlighted: variantTokens.textColor,
                    selected: variantTokens.textColor
                },
                background: {
                    normal: variantTokens.backgroundColor,
                    hovered: variantTokens.backgroundHoverColor,
                    highlighted: variantTokens.backgroundActiveColor,
                    focused: variantTokens.backgroundColor
                }
            }
        }
        
        let textColor = RHColor.textWhite
        let bgNormal, bgHover, bgActive, bgFocus
        
        // Logic to select correct BSColor sets
        switch (variant) {
            case "secondary":
                bgNormal = RHColor.secondary
                bgHover = RHColor.secondaryHover
                bgActive = RHColor.secondaryActive
                bgFocus = RHColor.secondaryHover
                break
            case "success":
                bgNormal = RHColor.success
                bgHover = RHColor.successHover
                bgActive = RHColor.successActive
                bgFocus = RHColor.successHover
                break
            case "danger":
                bgNormal = RHColor.danger
                bgHover = RHColor.dangerHover
                bgActive = RHColor.dangerActive
                bgFocus = RHColor.dangerHover
                break
            case "warning":
                textColor = RHColor.textBlack
                bgNormal = RHColor.warning
                bgHover = RHColor.warningHover
                bgActive = RHColor.warningActive
                bgFocus = RHColor.warningHover
                break
            case "info":
                textColor = RHColor.textBlack
                bgNormal = RHColor.info
                bgHover = RHColor.infoHover
                bgActive = RHColor.infoActive
                bgFocus = RHColor.infoHover
                break
            case "light":
                textColor = RHColor.textBlack
                bgNormal = RHColor.light
                bgHover = RHColor.lightHover
                bgActive = RHColor.lightActive
                bgFocus = RHColor.lightHover
                break
            case "dark":
                bgNormal = RHColor.dark
                bgHover = RHColor.darkHover
                bgActive = RHColor.darkActive
                bgFocus = RHColor.darkHover
                break
            case "link":
                textColor = RHColor.primary
                bgNormal = UIColor.transparentColor
                bgHover = UIColor.transparentColor
                bgActive = UIColor.transparentColor
                bgFocus = UIColor.transparentColor
                break
            case "primary":
            default:
                bgNormal = RHColor.primary
                bgHover = RHColor.primaryHover
                bgActive = RHColor.primaryActive
                bgFocus = RHColor.primaryHover
                break
        }
        
        return {
            titleLabel: {
                normal: textColor,
                hovered: textColor,
                highlighted: textColor,
                selected: textColor
            },
            background: {
                normal: bgNormal,
                hovered: bgHover,
                highlighted: bgActive,
                focused: bgFocus
            }
        }
    }
    
    // Helper to generate gradient style dynamically for any color
    _gradientStyles(baseColor: UIColor, isInset: boolean = NO, isHovered: boolean = NO) {
        
        // Do not apply gradient/shadow to Link variant
        if (this._variant === "link") {
            return {
                backgroundImage: "none",
                boxShadow: "none"
            }
        }
        
        const variantTokens = this.designTokensForVariant(this._variant)
        if (variantTokens) {
            return {
                backgroundImage: isInset
                                 ? variantTokens.gradientActive
                                 : isHovered
                                   ? variantTokens.gradientHover
                                   : variantTokens.gradient,
                boxShadow: isInset
                           ? variantTokens.shadowActive
                           : isHovered
                             ? variantTokens.shadowHover
                             : variantTokens.shadow
            }
        }
        
        // Create lighter color (Highlight) and darker color (Shadow)
        const lighterColor = baseColor.colorByMultiplyingRGB(1 + RHButton.sheenIntensity)
        const darkerColor = baseColor.colorByMultiplyingRGB(1 - RHButton.sheenIntensity)
        
        // Gradient: Light (Top Left) -> Base (Middle) -> Dark (Bottom Right)
        const gradientString = `linear-gradient(165deg, ${lighterColor.stringValue} 0%, ${baseColor.stringValue} 50%, ${darkerColor.stringValue} 100%)`
        
        // Use shadow color defined for variant
        const shadowColor = this._shadowColorForVariant(this._variant)
        let shadowString: string
        
        if (isInset) {
            // Pressed state
            shadowString = "inset 0 3px 5px rgba(0, 0, 0, 0.125)"
        }
        else {
            // Drop shadow logic
            const shadowOpacity = 0.2
            
            if (isHovered) {
                // Increased shadow for hover (lifted effect)
                shadowString = `0 5px 15px 2px ${shadowColor.colorWithAlpha(shadowOpacity).stringValue}`
            }
            else {
                // Normal shadow
                shadowString = `0 2px 6px 0px ${shadowColor.colorWithAlpha(shadowOpacity).stringValue}`
            }
        }
        
        return {
            backgroundImage: gradientString,
            boxShadow: shadowString
        }
    }
    
    
    override updateContentForNormalState() {
        super.updateContentForNormalState()
        
        const config = this._colorConfigForVariant(this._variant)
        const styles = this._gradientStyles(config.background.normal, NO, NO)
        
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                ...styles,
                borderColor: this._borderColorForState("normal").stringValue,
                outline: "none",
                outlineOffset: "0px"
            }
        })
        
        this.style.transition = RHButtons.transition
        
        // Reset Lift (Frame Transform) - not used anymore but good hygiene
        this.frameTransform = ""
        
        // Reset Label Transform to center
        if (this.titleLabel) {
            this.titleLabel.style.transform = ""
        }
        
        this._rippleView.style.transform = "translate(-50%, -50%) scale(0)"
        this._rippleView.alpha = 0
    }
    
    override updateContentForHoveredState() {
        super.updateContentForHoveredState()
        
        const config = this._colorConfigForVariant(this._variant)
        const styles = this._gradientStyles(config.background.hovered, NO, YES) // YES = hover shadow
        
        this.style.borderColor = this._borderColorForState("hover").stringValue
        
        // Apply new gradient and shadow for hover state
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                ...styles,
                outline: "none",
                outlineOffset: "0px"
            }
        })
        
        this.style.transition = RHButtons.transition
        
        // Ensure Label stays centered
        if (this.titleLabel) {
            this.titleLabel.style.transform = ""
        }
        
        // Ripple Effect from center
        this._rippleView.alpha = 1
        this._rippleView.style.transform = "translate(-50%, -50%) scale(1)"
    }
    
    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        
        const config = this._colorConfigForVariant(this._variant)
        const styles = this._gradientStyles(config.background.highlighted, YES, NO) // YES = inset shadow
        
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                ...styles,
                borderColor: this._borderColorForState("active").stringValue,
                outline: "none",
                outlineOffset: "0px"
            }
        })
        
        this.style.transition = RHButtons.transition
        
        // Move Label Down to indicate press
        if (this.titleLabel) {
            // Instant transform change
            this.titleLabel.style.transition = ""
            this.titleLabel.style.transform = "translateY(1px)"
        }
    }
    
    override updateContentForSelectedState() {
        
        super.updateContentForSelectedState()
        
        const config = this._colorConfigForVariant(this._variant)
        const styles = this._gradientStyles(config.background.highlighted, YES, NO) // YES = inset shadow
        
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                ...styles,
                borderColor: this._borderColorForState("active").stringValue,
                outline: "none",
                outlineOffset: "0px"
            }
        })
        
        this.style.transition = RHButtons.transition
        
        // Move Label Down to indicate press
        if (this.titleLabel) {
            // Instant transform change
            this.titleLabel.style.transition = ""
            this.titleLabel.style.transform = "translateY(1px)"
        }
    }
    
    override updateContentForFocusedState() {
        super.updateContentForFocusedState()
        
        const config = this._colorConfigForVariant(this._variant)
        const styles = this._gradientStyles(config.background.normal, NO, NO)
        
        // @ts-ignore
        this.configureWithObject({
            // @ts-ignore
            style: {
                ...styles,
                borderColor: this._borderColorForState("normal").stringValue,
                outline: this.focusOutlineForVariant(this._variant),
                outlineOffset: RHButtons.focusOutlineOffset
            }
        })
        
        this.style.transition = RHButtons.transition
        
        // Keep label centered after focus changes.
        if (this.titleLabel) {
            this.titleLabel.style.transform = ""
        }
        
        if (this.hovered) {
            this._rippleView.alpha = 1
            this._rippleView.style.transform = "translate(-50%, -50%) scale(1)"
        }
    }
    
    _borderColorForState(state: "normal" | "hover" | "active"): UIColor {
        const variantTokens = this.designTokensForVariant(this._variant)
        if (variantTokens) {
            switch (state) {
                case "hover":
                    return variantTokens.borderHoverColor
                case "normal":
                    return variantTokens.borderColor
                default:
                    return variantTokens.borderActiveColor
            }
        }
        
        switch (this._variant) {
            case "secondary":
                switch (state) {
                    case "hover":
                        return RHColor.secondaryBorderHover
                    case "normal":
                        return RHColor.secondaryBorder
                    default:
                        return RHColor.secondaryBorderActive
                }
            case "success":
                switch (state) {
                    case "hover":
                        return RHColor.successBorderHover
                    case "normal":
                        return RHColor.successBorder
                    default:
                        return RHColor.successBorderActive
                }
            case "danger":
                switch (state) {
                    case "normal":
                        return RHColor.dangerBorder
                    case "hover":
                        return RHColor.dangerBorderHover
                    default:
                        return RHColor.dangerBorderActive
                }
            case "warning":
                switch (state) {
                    case "normal":
                        return RHColor.warningBorder
                    case "hover":
                        return RHColor.warningBorderHover
                    default:
                        return RHColor.warningBorderActive
                }
            case "info":
                switch (state) {
                    case "normal":
                        return RHColor.infoBorder
                    case "hover":
                        return RHColor.infoBorderHover
                    default:
                        return RHColor.infoBorderActive
                }
            case "light":
                switch (state) {
                    case "normal":
                        return RHColor.lightBorder
                    case "hover":
                        return RHColor.lightBorderHover
                    default:
                        return RHColor.lightBorderActive
                }
            case "dark":
                switch (state) {
                    case "normal":
                        return RHColor.darkBorder
                    case "hover":
                        return RHColor.darkBorderHover
                    default:
                        return RHColor.darkBorderActive
                }
            case "link":
                return RHColor.transparentColor
            default:
                switch (state) {
                    case "normal":
                        return RHColor.primaryBorder
                    case "hover":
                        return RHColor.primaryBorderHover
                    default:
                        return RHColor.primaryBorderActive
                }
        }
    }
    
    _shadowColorForVariant(variant: RHButtonVariant) {
        const variantTokens = this.designTokensForVariant(variant)
        if (variantTokens) {
            return variantTokens.backgroundColor
        }
        
        const map = {
            softWarning: RHColor.warningShadow,
            softDanger: RHColor.dangerShadow,
            tableActionSuccess: BSColor.success,
            secondary: RHColor.secondaryShadow,
            messageAnswer: RHColor.primaryShadow,
            messageCopy: RHColor.primaryShadow,
            success: RHColor.successShadow,
            danger: RHColor.dangerShadow,
            warning: RHColor.warningShadow,
            info: RHColor.infoShadow,
            light: RHColor.lightShadow,
            dark: RHColor.darkShadow,
            link: RHColor.colorWithRGBA(13, 110, 253, 0.25),
            primary: RHColor.primaryShadow
        } as const
        // @ts-ignore
        const result: ValueOf<typeof map> = map[variant] ?? RHColor.primaryShadow
        return result
    }
    
}
